import {RefObject, useCallback, useEffect, useLayoutEffect, useState} from 'react'
import {log} from 'common/log'
import _ from 'lodash'
import {getScrollParent} from 'util/view/getScrollParent'

const MAX_HEIGHT = 100
const MIN_WIDTH = 400
const SPACER_HEIGHT = 4
export const SCROLLBAR_SIZE = 10

interface Args {
	refWrapper: RefObject<HTMLDivElement | null> // positioned to ref
	open: boolean
	maxHeight?: number
	minWidth?: number
}

function getPos({ref}: { ref: RefObject<HTMLDivElement | null> }) {
	const {top, left, height, width} = ref.current?.getBoundingClientRect() || {}
	return {top, left, height, width}
}

export function useDropDownMenuRect(args: Args) {
	const {
		refWrapper,
		open,
		maxHeight: configuredMaxHeight,
		minWidth: configuredMinWidth,
	} = args
	const [pos, setPos] = useState(getPos({ref: refWrapper}))

	let handleResize = useCallback(_.debounce(function handleResize<K extends keyof WindowEventMap>(evt?: WindowEventMap[K]) {
		const p = getPos({ref: refWrapper})
		log.trace('Slct resize pos', evt, p)
		setPos(p)
	}, 5), [refWrapper.current])

	useLayoutEffect(() => {
		handleResize()
		handleResize.flush()
		log.debug('Slct first handleResize', refWrapper)
	}, [/* only called once, therefore no dependencies wanted! */]);

	useEffect(() => {
		if (!open || !refWrapper.current) return;
		// there may be needed a repositioning
		// 350ms: wait for animations settled:
		// 		(1) input scroll into view
		// 		(2) PanelWrapContent resize animation for side panel in and out
		handleResize() // reposition again after debounce timeout, for cases when first positioning was not correct or something moved
		const scrollParent = getScrollParent(refWrapper.current)
		if (!scrollParent) log.error('Slct no scrollParent found!', refWrapper.current, scrollParent)
		else log.trace('Slct attach handleResize', refWrapper.current, scrollParent)
		window.addEventListener('resize', handleResize)
		scrollParent?.addEventListener('scroll', handleResize)
		return () => {
			window.removeEventListener('resize', handleResize)
			scrollParent?.removeEventListener('scroll', handleResize)
		}
	}, [refWrapper.current, window, open])
	let top = ((pos.top || 0) + (pos.height || 0)) + SPACER_HEIGHT
	let left = (pos.left || 0) - 1 // -1border
	let width = Math.max((pos.width || 0) - 8, configuredMinWidth || MIN_WIDTH) + SCROLLBAR_SIZE
	const availableHeight = window.innerHeight - top - SCROLLBAR_SIZE
	let height = Math.min(configuredMaxHeight || MAX_HEIGHT, availableHeight) + SCROLLBAR_SIZE
	if (top + height > window.innerHeight) {
		top = window.innerHeight - height + SPACER_HEIGHT
		if (top < 0) {
			top = 0
			height = Math.min(window.innerHeight, height - SCROLLBAR_SIZE)
		}
	}
	if (left + width > window.innerWidth) {
		left = window.innerWidth - width
		if (left < 0) {
			left = 0
			width = Math.min(window.innerWidth, width)
		}
	}
	return {top, left, width, height}
}